/** Category codes, labels, routes and plain-language blurbs for People > Benefits and Deductions (docs/benefits-deductions-contract.md sections 1 and 2).
 * The codes are stable wire values (routes, API, seed data) and are never renumbered. */

export const BENEFIT_CATEGORY_CODES = [
  'health-wellness',
  'insurance',
  'retirement-savings',
  'financial-equity',
  'family-lifestyle',
  'leave-wellbeing',
  'other-custom',
] as const;
export type BenefitCategoryCode = (typeof BENEFIT_CATEGORY_CODES)[number];

export const DEDUCTION_CATEGORY_CODES = [
  'benefit-premiums',
  'retirement-contributions',
  'garnishments-levies',
  'loans-repayments',
  'union-charitable-other',
  'taxes-statutory',
  'other-custom',
] as const;
export type DeductionCategoryCode = (typeof DEDUCTION_CATEGORY_CODES)[number];

export const BENEFIT_CATEGORY_LABELS: Record<BenefitCategoryCode, string> = {
  'health-wellness': 'Health & Wellness',
  insurance: 'Insurance',
  'retirement-savings': 'Retirement & Savings',
  'financial-equity': 'Financial & Equity',
  'family-lifestyle': 'Family & Lifestyle',
  'leave-wellbeing': 'Leave & Well-Being',
  'other-custom': 'Other & Custom Benefits',
};

export const DEDUCTION_CATEGORY_LABELS: Record<DeductionCategoryCode, string> = {
  'benefit-premiums': 'Benefit Premiums',
  'retirement-contributions': 'Retirement Contributions',
  'garnishments-levies': 'Garnishments & Levies',
  'loans-repayments': 'Loans & Repayments',
  'union-charitable-other': 'Union, Charitable & Other',
  'taxes-statutory': 'Taxes & Statutory Deductions',
  'other-custom': 'Other & Custom Deductions',
};

export const BENEFIT_CATEGORY_BLURBS: Record<BenefitCategoryCode, string> = {
  'health-wellness': 'Medical, dental, vision and other health and wellness plans your company offers.',
  insurance: 'Life, disability and other insurance plans your company offers.',
  'retirement-savings': 'Retirement and savings plans, including employer contributions.',
  'financial-equity': 'Bonus, equity and financial assistance programs.',
  'family-lifestyle': 'Family, commuter and lifestyle programs.',
  'leave-wellbeing': 'Leave programs your company offers. Time and Leave owns balances and accruals.',
  'other-custom': 'Benefits that do not fit another category. Create your own here.',
};

export const DEDUCTION_CATEGORY_BLURBS: Record<DeductionCategoryCode, string> = {
  'benefit-premiums': 'Employee premium and contribution deductions for benefit plans.',
  'retirement-contributions': 'Employee retirement contribution deductions.',
  'garnishments-levies': 'Court-ordered and agency withholdings. These are involuntary and need rule review.',
  'loans-repayments': 'Loan, advance and other authorized repayment deductions.',
  'union-charitable-other': 'Union dues, charitable and other voluntary deductions.',
  'taxes-statutory': 'Taxes and other statutory deductions. The system calculates these; you cannot change them here.',
  'other-custom': 'Deductions that do not fit another category. Create your own here.',
};

export const BENEFITS_BASE_PATH = '/people/benefits';
export const DEDUCTIONS_BASE_PATH = '/people/deductions';

export function benefitCategoryPath(code: BenefitCategoryCode): string {
  return `${BENEFITS_BASE_PATH}/${code}`;
}

export function deductionCategoryPath(code: DeductionCategoryCode): string {
  return `${DEDUCTIONS_BASE_PATH}/${code}`;
}

export function isBenefitCategory(value: string | null | undefined): value is BenefitCategoryCode {
  return !!value && (BENEFIT_CATEGORY_CODES as readonly string[]).includes(value);
}

export function isDeductionCategory(value: string | null | undefined): value is DeductionCategoryCode {
  return !!value && (DEDUCTION_CATEGORY_CODES as readonly string[]).includes(value);
}
